// The Knowledge base "Manuals library" tab (Session BAV). The office's view of the Drive manuals library: the folder
// the platform reads, its monthly vision page budget and limits (an admin changes them; the server refuses anyone
// else with a 403, said here in plain English), the last sweep of the folder, a Run now and a Retry all failed, and
// every file found there with what happened to it.
//
// The file list is SERVER filtered, sorted and paginated (it can hold thousands of files), so the filters, the search,
// the sort headers and the pages all go to the server; nothing is filtered client side over one page.
//
// While any file is still being worked on, the status and the current page refresh every 15 seconds as BACKGROUND
// requests (the Session ASB idle rule: a timer's request never keeps an office session alive). The refresh stops on
// its own once nothing is working.
//
// Nothing here touches the original files in Drive. "Remove from the knowledge base" removes the document's search
// entries and page pictures only; "Forget" removes the record of a file that is no longer in Drive.
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import { useOptionalAuthUser } from "../../lib/auth";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { KnowledgeRebuildDialog } from "./KnowledgeRebuildDialog";
import { formatDateTime } from "../../lib/format";
import { Button } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { OverflowMenu, overflowDangerItemCls, overflowItemCls } from "../../components/OverflowMenu";
import { StatusPill } from "../../components/StatusPill";
import { inputCls, labelCls } from "./knowledgeUi";
import { KnowledgeLibraryNamesCard } from "./KnowledgeLibraryNamesCard";
import { KnowledgeMakerIdentityCard } from "./KnowledgeMakerIdentityCard";
import { KnowledgeVocabularyCard } from "./KnowledgeVocabularyCard";
import {
  KNOWLEDGE_DRIVE_STATUSES,
  KNOWLEDGE_DRIVE_STATUS_LABEL,
  driveIdFromInput,
  formatFileSize,
  knowledgeDriveStatusPill,
  knowledgeShortDate,
  type KnowledgeDriveActionResult,
  type KnowledgeDriveFileListResult,
  type KnowledgeDriveFileRow,
  type KnowledgeDriveSettings,
  type KnowledgeDriveSort,
  type KnowledgeDriveStatus,
  type KnowledgeDriveSkippedAccounting,
  type KnowledgeDriveStatusResponse,
} from "../../lib/knowledgeTypes";

export const DRIVE_PAGE_SIZE = 25;

// The statuses a file passes through while the platform is still working on it.
export const WORKING_STATUSES: KnowledgeDriveStatus[] = ["QUEUED", "DOWNLOADING", "EXTRACTING", "OCR", "VISION"];

// The kinds of file the library sorts files into (the server's FileFamily), in plain English for the Type filter.
export const FILE_FAMILY_LABEL: Record<string, string> = {
  pdf: "PDF",
  word: "Word document",
  spreadsheet: "Spreadsheet",
  presentation: "Presentation",
  text: "Text file",
  html: "Web page",
  image: "Picture",
  archive: "Archive (zip)",
  audio: "Audio",
  video: "Video",
  cad: "CAD drawing",
  email: "Email",
  zdl: "Disc label",
  other: "Other",
};

export function familyLabel(family: string | null): string {
  if (!family) return "Not known";
  return FILE_FAMILY_LABEL[family] ?? family;
}

const FOLDER_SOURCE_LABEL: Record<KnowledgeDriveSettings["folderSource"], string> = {
  setting: "Set here",
  env: "Set on the server",
  none: "Not set",
};

// The amber notice panel, with a theme-mapped ink (the AUK rule) and the locked amber line.
const NOTICE_CLS = "rounded-ele border border-[var(--ele-amber-text-deep)] bg-ele-surface px-3 py-2 text-sm text-amber-300";
const QUIET_BTN =
  "max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong disabled:opacity-40";

type Feedback = { kind: "ok" | "error"; text: string } | null;

function pagesText(row: KnowledgeDriveFileRow): string {
  const working = WORKING_STATUSES.includes(row.status);
  if (working && row.pageCount != null) return `${row.pagesDone} of ${row.pageCount}`;
  if (row.pageCount != null) return String(row.pageCount);
  return working && row.pagesDone > 0 ? `${row.pagesDone} so far` : "Not known";
}

function memberArchiveName(row: KnowledgeDriveFileRow): string {
  // A file found inside an archive carries the archive's path; the archive's own name is its first segment.
  const path = row.memberPath ?? "";
  const first = path.split("/").filter(Boolean)[0];
  return first ?? "an archive";
}

export function KnowledgeDriveTab() {
  const [status, setStatus] = useState<KnowledgeDriveStatusResponse | null>(null);
  const [statusError, setStatusError] = useState(false);
  const [result, setResult] = useState<KnowledgeDriveFileListResult | null>(null);
  const [listError, setListError] = useState(false);

  const [statusFilter, setStatusFilter] = useState<KnowledgeDriveStatus | "">("");
  const [manufacturer, setManufacturer] = useState("");
  const [family, setFamily] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [missingOnly, setMissingOnly] = useState(false);
  const [sort, setSort] = useState<KnowledgeDriveSort>("name");
  const [dir, setDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);

  const [feedback, setFeedback] = useState<Feedback>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<KnowledgeDriveFileRow | null>(null);
  const [forgetTarget, setForgetTarget] = useState<KnowledgeDriveFileRow | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  // The restore rebuild is an admin's, as its routes are (Session SCR1).
  const user = useOptionalAuthUser();
  const isAdmin = user?.role === "admin";
  const [rebuildOpen, setRebuildOpen] = useState(false);

  // The search box is debounced so a request does not fire per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const query = {
    status: statusFilter || undefined,
    manufacturer: manufacturer || undefined,
    family: family || undefined,
    search: search || undefined,
    sourceMissing: missingOnly ? true : undefined,
    sort,
    dir,
    page,
    pageSize: DRIVE_PAGE_SIZE,
  };
  const queryKey = JSON.stringify(query);
  const queryRef = useRef(query);
  queryRef.current = query;

  const loadStatus = useCallback((background: boolean) => {
    if (!background) setStatusError(false);
    return api.knowledge.drive
      .status(background ? { background: true } : {})
      .then((s) => {
        setStatus(s);
        setStatusError(false);
      })
      .catch(() => {
        if (!background) setStatusError(true);
      });
  }, []);

  const loadFiles = useCallback((background: boolean) => {
    if (!background) setListError(false);
    return api.knowledge.drive
      .files(queryRef.current, background ? { background: true } : {})
      .then((r) => {
        setResult(r);
        setListError(false);
      })
      .catch(() => {
        if (!background) setListError(true);
      });
  }, []);

  const refresh = useCallback(
    (background: boolean) => {
      void loadStatus(background);
      void loadFiles(background);
    },
    [loadStatus, loadFiles],
  );

  useEffect(() => {
    void loadStatus(false);
  }, [loadStatus]);

  useEffect(() => {
    void loadFiles(false);
    // queryKey stands for every filter, the sort and the page.
  }, [queryKey, loadFiles]);

  const working =
    (status ? WORKING_STATUSES.some((s) => (status.counts[s] ?? 0) > 0) : false) ||
    (result?.items.some((r) => WORKING_STATUSES.includes(r.status)) ?? false);

  // Refresh while anything is still being worked on; a background request each time (the idle rule).
  useEffect(() => {
    if (!working) return;
    const t = setInterval(() => void refresh(true), 15000);
    return () => clearInterval(t);
  }, [working, refresh]);

  async function runAction(key: string, action: () => Promise<KnowledgeDriveActionResult>, fallback: string) {
    setBusy(key);
    setFeedback(null);
    try {
      const res = await action();
      setFeedback({ kind: res.ok ? "ok" : "error", text: res.message || (res.ok ? "Done." : fallback) });
      refresh(false);
      return res;
    } catch (err) {
      setFeedback({ kind: "error", text: friendlyApiError(err, fallback) });
      return null;
    } finally {
      setBusy(null);
    }
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    const target = removeTarget;
    setModalError(null);
    setBusy(`remove:${target.id}`);
    try {
      const res = await api.knowledge.drive.remove(target.id);
      if (!res.ok) {
        setModalError(res.message || "That file could not be removed. Please try again.");
        return;
      }
      setRemoveTarget(null);
      setFeedback({ kind: "ok", text: res.message || "Removed from the knowledge base." });
      refresh(false);
    } catch (err) {
      setModalError(friendlyApiError(err, "That file could not be removed. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  async function confirmForget() {
    if (!forgetTarget) return;
    const target = forgetTarget;
    setModalError(null);
    setBusy(`forget:${target.id}`);
    try {
      const res = await api.knowledge.drive.forget(target.id);
      if (!res.ok) {
        setModalError(res.message || "That file could not be forgotten. Please try again.");
        return;
      }
      setForgetTarget(null);
      setFeedback({ kind: "ok", text: res.message || "The file has been forgotten." });
      refresh(false);
    } catch (err) {
      setModalError(friendlyApiError(err, "That file could not be forgotten. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  function changeSort(next: KnowledgeDriveSort) {
    if (sort === next) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSort(next);
      setDir(next === "lastSyncedAt" || next === "size" ? "desc" : "asc");
    }
    setPage(1);
  }

  function chooseStatus(next: KnowledgeDriveStatus | "") {
    setStatusFilter(next);
    setPage(1);
  }

  const totalPages = result ? Math.max(1, Math.ceil(result.total / (result.pageSize || DRIVE_PAGE_SIZE))) : 1;
  const failedCount = status?.counts.FAILED ?? 0;

  return (
    <div className="space-y-4">
      {statusError ? (
        <div role="alert" className="rounded-ele border border-ele-danger bg-ele-surface p-4 text-sm text-ele-strong">
          Could not load the manuals library settings.
          <button type="button" onClick={() => void loadStatus(false)} className={`ml-3 ${QUIET_BTN}`}>
            Retry
          </button>
        </div>
      ) : status === null ? (
        <Loader label="Loading the manuals library..." shape="panel" />
      ) : (
        <>
          <SettingsCard settings={status.settings} onSaved={(s) => setStatus((prev) => (prev ? { ...prev, settings: s } : prev))} />
          <KnowledgeLibraryNamesCard />
          <KnowledgeMakerIdentityCard />
          <KnowledgeVocabularyCard />
          <LastSweepCard
            status={status}
            busy={busy}
            failedCount={failedCount}
            onRebuild={isAdmin ? () => setRebuildOpen(true) : undefined}
            onRun={() => void runAction("sweep", () => api.knowledge.drive.sweep(), "The sweep could not be started. Please try again.")}
            onRetryFailed={() =>
              void runAction("retryFailed", () => api.knowledge.drive.retryFailed(), "The failed files could not be queued again. Please try again.")
            }
          />
          <StatusCounts status={status} selected={statusFilter} onSelect={chooseStatus} />
          {status.skipped && status.skipped.total > 0 && <SkippedAccounting skipped={status.skipped} onShowSkipped={() => chooseStatus("SKIPPED")} />}
        </>
      )}

      {feedback &&
        (feedback.kind === "ok" ? (
          <div role="status" className="flex items-start justify-between gap-3 rounded-ele border border-ele-hairline bg-ele-surface p-3">
            <p className="text-sm text-ele-strong">{feedback.text}</p>
            <button type="button" onClick={() => setFeedback(null)} className="text-xs text-ele-body underline hover:text-ele-strong">
              Dismiss
            </button>
          </div>
        ) : (
          <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {feedback.text}
          </p>
        ))}

      {/* Filters: every one goes to the server. */}
      <div className="grid gap-3 rounded-ele border border-ele-purple-accent bg-ele-surface p-4 sm:grid-cols-2 lg:grid-cols-5">
        <label className="lg:col-span-2">
          <span className={labelCls}>Search</span>
          <input
            aria-label="Search the manuals library"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            placeholder="File name or folder"
            className={inputCls}
          />
        </label>
        <label>
          <span className={labelCls}>Status</span>
          <select aria-label="Filter by status" value={statusFilter} onChange={(e) => chooseStatus(e.target.value as KnowledgeDriveStatus | "")} className={inputCls}>
            <option value="">All statuses</option>
            {KNOWLEDGE_DRIVE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {KNOWLEDGE_DRIVE_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className={labelCls}>Manufacturer</span>
          <select
            aria-label="Filter by manufacturer"
            value={manufacturer}
            onChange={(e) => {
              setManufacturer(e.target.value);
              setPage(1);
            }}
            className={inputCls}
          >
            <option value="">All manufacturers</option>
            {(result?.manufacturers ?? []).map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className={labelCls}>Type</span>
          <select
            aria-label="Filter by type"
            value={family}
            onChange={(e) => {
              setFamily(e.target.value);
              setPage(1);
            }}
            className={inputCls}
          >
            <option value="">All types</option>
            {Object.entries(FILE_FAMILY_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-ele-body sm:col-span-2 lg:col-span-5">
          <input
            type="checkbox"
            checked={missingOnly}
            onChange={(e) => {
              setMissingOnly(e.target.checked);
              setPage(1);
            }}
            className="accent-ele-green"
          />
          Missing from Drive only
        </label>
      </div>

      {listError ? (
        <div role="alert" className="rounded-ele border border-ele-danger bg-ele-surface p-6 text-center">
          <p className="text-ele-strong">Could not load the files. Please try again.</p>
          <button type="button" onClick={() => void loadFiles(false)} className={`mt-3 ${QUIET_BTN}`}>
            Retry
          </button>
        </div>
      ) : result === null ? (
        <Loader label="Loading the files..." />
      ) : result.items.length === 0 ? (
        <EmptyState
          role="status"
          message={
            result.total === 0 && !statusFilter && !manufacturer && !family && !search && !missingOnly
              ? "No files found in the library yet. Set the folder above and press Run now."
              : "No files match these filters."
          }
        />
      ) : (
        <FilesTable
          rows={result.items}
          sort={sort}
          dir={dir}
          busy={busy}
          onSort={changeSort}
          onRetry={(row) => void runAction(`retry:${row.id}`, () => api.knowledge.drive.retry(row.id, "retry"), "That file could not be queued again. Please try again.")}
          onReingest={(row) =>
            void runAction(`reingest:${row.id}`, () => api.knowledge.drive.retry(row.id, "reingest"), "That file could not be read in again. Please try again.")
          }
          onRemove={(row) => {
            setModalError(null);
            setRemoveTarget(row);
          }}
          onForget={(row) => {
            setModalError(null);
            setForgetTarget(row);
          }}
        />
      )}

      {result !== null && result.total > 0 && (
        <div className="flex items-center justify-between gap-3 text-sm text-ele-body">
          <span>
            Page {page} of {totalPages} ({result.total} file{result.total === 1 ? "" : "s"})
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className={QUIET_BTN}>
              Previous
            </button>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className={QUIET_BTN}>
              Next
            </button>
          </div>
        </div>
      )}

      {rebuildOpen && (
        <KnowledgeRebuildDialog
          onClose={() => {
            setRebuildOpen(false);
            refresh(false);
          }}
        />
      )}
      <ConfirmDeleteModal
        open={removeTarget !== null}
        title="Remove from the knowledge base"
        message={`This removes "${removeTarget?.name ?? ""}" from the knowledge base: its search entries and page pictures are deleted. The original file in Drive is NOT touched. The file stays listed here as Removed until you re-ingest it.`}
        confirmLabel="Remove"
        variant="destructive"
        busy={removeTarget !== null && busy === `remove:${removeTarget.id}`}
        error={removeTarget ? modalError : null}
        onConfirm={() => void confirmRemove()}
        onCancel={() => {
          setRemoveTarget(null);
          setModalError(null);
        }}
      />
      <ConfirmDeleteModal
        open={forgetTarget !== null}
        title="Forget this file"
        message={`"${forgetTarget?.name ?? ""}" is no longer in the Drive folder. Forgetting it removes its record here and anything the knowledge base holds for it. Nothing in Drive is touched.`}
        confirmLabel="Forget"
        variant="destructive"
        busy={forgetTarget !== null && busy === `forget:${forgetTarget.id}`}
        error={forgetTarget ? modalError : null}
        onConfirm={() => void confirmForget()}
        onCancel={() => {
          setForgetTarget(null);
          setModalError(null);
        }}
      />
    </div>
  );
}

// ---- Settings ----

function SettingsCard({ settings, onSaved }: { settings: KnowledgeDriveSettings; onSaved: (s: KnowledgeDriveSettings) => void }) {
  const [editing, setEditing] = useState(false);
  const [folder, setFolder] = useState("");
  const [budget, setBudget] = useState("");
  const [maxMb, setMaxMb] = useState("");
  const [ocr, setOcr] = useState("");
  const [diskGb, setDiskGb] = useState("");
  const [minFreeGb, setMinFreeGb] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  function startEdit() {
    setFolder(settings.folderSource === "setting" ? settings.folderId ?? "" : "");
    setBudget(settings.visionBudgetIsDefault ? "" : String(settings.visionMonthlyPageBudget));
    setMaxMb(settings.maxFileIsDefault ? "" : String(settings.maxFileMb));
    setOcr(settings.ocrLanguages.join(", "));
    setDiskGb(settings.dbDiskIsDefault ? "" : String(settings.dbDiskGb));
    setMinFreeGb(String(settings.dbMinFreeGb));
    setError(null);
    setSaved(false);
    setEditing(true);
  }

  function wholeNumber(raw: string): number | null | "bad" {
    const s = raw.trim();
    if (!s) return null;
    if (!/^\d+$/.test(s)) return "bad";
    const n = Number(s);
    return n > 0 ? n : "bad";
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    const budgetValue = wholeNumber(budget);
    const maxValue = wholeNumber(maxMb);
    if (budgetValue === "bad") {
      setError("The monthly vision page budget must be a whole number above 0, or left blank for the default.");
      return;
    }
    if (maxValue === "bad") {
      setError("The largest file size must be a whole number of MB above 0, or left blank for the default.");
      return;
    }
    const diskValue = wholeNumber(diskGb);
    if (diskValue === "bad") {
      setError("The database disk size must be a whole number of GB above 0, or left blank for the default.");
      return;
    }
    const minFreeRaw = minFreeGb.trim();
    if (minFreeRaw && !/^\d+$/.test(minFreeRaw)) {
      setError("The space to keep free must be a whole number of GB, or left blank for the default.");
      return;
    }
    const folderId = folder.trim() ? driveIdFromInput(folder, "folder") : null;
    setSaving(true);
    setError(null);
    try {
      const res = await api.knowledge.drive.saveSettings({
        folderId,
        visionMonthlyPageBudget: budgetValue,
        maxFileMb: maxValue,
        ocrLanguages: ocr.trim() || null,
        // The disk guard's two values are sent only when changed, so saving another setting never pins a default.
        ...(diskGb.trim() !== (settings.dbDiskIsDefault ? "" : String(settings.dbDiskGb)) ? { dbDiskGb: diskValue } : {}),
        ...(minFreeRaw !== String(settings.dbMinFreeGb) ? { dbMinFreeGb: minFreeRaw ? Number(minFreeRaw) : null } : {}),
      });
      onSaved(res.settings);
      setEditing(false);
      setSaved(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setError("Only an admin can change this.");
      else setError(friendlyApiError(err, "The settings could not be saved. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-lg text-ele-blue">Manuals library settings</h2>
        {!editing && (
          <button type="button" onClick={startEdit} className={QUIET_BTN}>
            Change settings
          </button>
        )}
      </div>

      {!settings.googleConnected ? (
        <p className={`mt-3 ${NOTICE_CLS}`}>Google is not connected. Connect it in Settings.</p>
      ) : settings.driveScopeOk === false ? (
        <p className={`mt-3 ${NOTICE_CLS}`}>
          The Google connection cannot read the library folder. Reconnect Google in Settings and allow Drive access.
        </p>
      ) : null}

      {saved && (
        <p role="status" className="mt-3 text-sm text-ele-strong">
          Settings saved.
        </p>
      )}

      {!editing ? (
        <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <div className="min-w-0">
            <dt className={labelCls}>Folder</dt>
            <dd className="break-all text-ele-strong">{settings.folderId ?? "Not set"}</dd>
            <dd className="text-xs text-ele-body opacity-80">
              {FOLDER_SOURCE_LABEL[settings.folderSource]}
              {settings.folderSource === "setting" && settings.folderUpdatedAt
                ? ` on ${formatDateTime(settings.folderUpdatedAt)}${settings.folderUpdatedByName ? ` by ${settings.folderUpdatedByName}` : ""}`
                : ""}
            </dd>
          </div>
          <div>
            <dt className={labelCls}>Vision pages this month</dt>
            <dd className="text-ele-strong">
              {settings.visionPagesUsedThisMonth.toLocaleString("en-GB")} of {settings.visionMonthlyPageBudget.toLocaleString("en-GB")} used
            </dd>
            <dd className="text-xs text-ele-body opacity-80">{settings.visionBudgetIsDefault ? "The default budget" : "Set here"}</dd>
          </div>
          <div>
            <dt className={labelCls}>Largest file read</dt>
            <dd className="text-ele-strong">{settings.maxFileMb} MB</dd>
            <dd className="text-xs text-ele-body opacity-80">{settings.maxFileIsDefault ? "The default" : "Set here"}</dd>
          </div>
          <div>
            <dt className={labelCls}>Scanned page languages (OCR)</dt>
            <dd className="text-ele-strong">{settings.ocrLanguages.length ? settings.ocrLanguages.join(", ") : "Not set"}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className={labelCls}>Pages waiting for the vision pass</dt>
            <dd className="text-ele-strong">{settings.pagesWaitingForVision.toLocaleString("en-GB")}</dd>
            <dd className="text-xs text-ele-body opacity-80">{visionWaitingText(settings)}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className={labelCls}>Database disk</dt>
            <dd className="text-ele-strong">{dbDiskText(settings)}</dd>
            <dd className="text-xs text-ele-body opacity-80">
              {settings.dbLibraryPaused
                ? "The library has paused to protect the database. Resize the disk on Render, then enter the new size here."
                : `The library pauses before the database has less than ${settings.dbMinFreeGb} GB free. ${settings.dbDiskIsDefault ? "The size is the default; enter the size Render shows for the database." : "Size entered here."}`}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className={labelCls}>Temporary working space</dt>
            <dd className="text-ele-strong">{tmpSpaceText(settings)}</dd>
            <dd className="text-xs text-ele-body opacity-80">
              Files are opened on the server one at a time per worker. A file waits in the queue when the working space is full, and is read when there is room.
            </dd>
          </div>
        </dl>
      ) : (
        <form onSubmit={save} className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="sm:col-span-2">
            <span className={labelCls}>Drive folder link or ID</span>
            <input
              aria-label="Drive folder link or ID"
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              placeholder="Paste the folder's share link or its ID. Leave blank to use the server's folder."
              className={inputCls}
            />
          </label>
          <label>
            <span className={labelCls}>Monthly vision page budget</span>
            <input
              aria-label="Monthly vision page budget"
              inputMode="numeric"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              placeholder="Blank for the default"
              className={inputCls}
            />
          </label>
          <label>
            <span className={labelCls}>Largest file size (MB)</span>
            <input
              aria-label="Largest file size in MB"
              inputMode="numeric"
              value={maxMb}
              onChange={(e) => setMaxMb(e.target.value)}
              placeholder="Blank for the default"
              className={inputCls}
            />
          </label>
          <label className="sm:col-span-2">
            <span className={labelCls}>Scanned page languages (OCR)</span>
            <input
              aria-label="OCR languages"
              value={ocr}
              onChange={(e) => setOcr(e.target.value)}
              placeholder="For example: eng"
              className={inputCls}
            />
          </label>
          <label>
            <span className={labelCls}>Database disk size (GB)</span>
            <input
              aria-label="Database disk size in GB"
              inputMode="numeric"
              value={diskGb}
              onChange={(e) => setDiskGb(e.target.value)}
              placeholder="Blank for the default (15)"
              className={inputCls}
            />
          </label>
          <label>
            <span className={labelCls}>Keep this much free (GB)</span>
            <input
              aria-label="Space to keep free in GB"
              inputMode="numeric"
              value={minFreeGb}
              onChange={(e) => setMinFreeGb(e.target.value)}
              placeholder="Blank for the default (2)"
              className={inputCls}
            />
          </label>
          <p className="text-xs text-ele-body opacity-80 sm:col-span-2">Only an admin can change these settings.</p>
          {error && (
            <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong sm:col-span-2">
              {error}
            </p>
          )}
          <div className="flex flex-wrap gap-2 sm:col-span-2">
            <Button type="submit" loading={saving} disabled={saving}>
              Save
            </Button>
            <button type="button" onClick={() => setEditing(false)} disabled={saving} className={QUIET_BTN}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

// What the pages waiting for the vision budget cost, said plainly. An estimate, never a cap: the cap is the page budget.
export function visionWaitingText(s: KnowledgeDriveSettings): string {
  if (s.pagesWaitingForVision === 0) return "None. Every page that needs the vision pass has had it.";
  const cost = `about $${s.visionWaitingCostUsd.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} at roughly $${s.visionCostPerPageUsd.toFixed(s.visionCostPerPageUsd < 0.01 ? 3 : 2)} a page (an estimate)`;
  const months =
    s.visionMonthsToClear === null
      ? "With no monthly budget they are never read."
      : `At ${s.visionMonthlyPageBudget.toLocaleString("en-GB")} pages a month that takes ${s.visionMonthsToClear} month${s.visionMonthsToClear === 1 ? "" : "s"}; raise the budget to read them sooner.`;
  return `Reading them would cost ${cost}. ${months} Until then each is searchable by the text OCR found.`;
}

export function dbDiskText(s: KnowledgeDriveSettings): string {
  if (s.dbUsedBytes === null || s.dbFreeBytes === null) return `${s.dbDiskGb} GB disk; the database's size could not be read just now`;
  const gb = (b: number) => (b / 1024 ** 3).toFixed(1);
  return `${gb(s.dbUsedBytes)} GB used of ${s.dbDiskGb} GB, ${gb(s.dbFreeBytes)} GB free`;
}

// The worker's temporary working space, as the worker last measured it (Session BBT).
export function tmpSpaceText(s: KnowledgeDriveSettings): string {
  if (s.tmpUsedBytes == null || s.tmpLimitBytes == null || !s.tmpMeasuredAt) return "Not measured yet: the server measures it when it next reads a file";
  const size = (b: number) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(1)} GB` : `${Math.round(b / 1024 ** 2)} MB`);
  return `${size(s.tmpUsedBytes)} in use of ${size(s.tmpLimitBytes)}, measured ${formatDateTime(s.tmpMeasuredAt)}`;
}

// ---- Last sweep and the two library-wide actions ----

function LastSweepCard({
  status,
  busy,
  failedCount,
  onRun,
  onRetryFailed,
  onRebuild,
}: {
  status: KnowledgeDriveStatusResponse;
  busy: string | null;
  failedCount: number;
  onRun: () => void;
  onRetryFailed: () => void;
  /** Admin only (the rebuild routes are): absent for anyone else, so no menu is drawn for them. */
  onRebuild?: () => void;
}) {
  const sweep = status.lastSweep;
  const summary = sweep.summary;
  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-lg text-ele-blue">Last sweep of the folder</h2>
        <div className="flex flex-wrap gap-2">
          <Button onClick={onRun} loading={busy === "sweep"} disabled={busy !== null}>
            Run now
          </Button>
          <ConfirmButton
            onConfirm={onRetryFailed}
            label={`Retry all failed (${failedCount})`}
            confirmLabel={`Press again to retry ${failedCount} file${failedCount === 1 ? "" : "s"}`}
            busy={busy === "retryFailed"}
            busyLabel="Queueing..."
            disabled={failedCount === 0 || busy !== null}
            className={QUIET_BTN}
          />
          {onRebuild && (
            // Session SCR1: the library rebuild after a restore. Rare and an admin's, so it sits in the overflow menu, not beside Run now.
            <OverflowMenu label="More library actions" closeOnSelect>
              <button type="button" role="menuitem" onClick={onRebuild} className={overflowItemCls}>
                Rebuild after a restore...
              </button>
            </OverflowMenu>
          )}
        </div>
      </div>
      {!sweep.at ? (
        <p className="mt-2 text-sm text-ele-body">No sweep has run yet.</p>
      ) : (
        <div className="mt-2 space-y-2 text-sm text-ele-body">
          <p>
            Started {formatDateTime(sweep.at)}.{" "}
            {summary?.complete || sweep.completeAt
              ? `Completed${sweep.completeAt ? ` ${formatDateTime(sweep.completeAt)}` : ""}.`
              : "It did not complete, so some files may not have been checked."}
          </p>
          {summary && (
            <p className="text-ele-strong">
              {summary.files} file{summary.files === 1 ? "" : "s"} found, {summary.added} new, {summary.changed} changed, {summary.missing} missing from Drive,{" "}
              {summary.queued} queued.
            </p>
          )}
          {sweep.error && <p className={NOTICE_CLS}>{sweep.error}</p>}
          {summary && summary.problems.length > 0 && (
            <div>
              <p className="font-menu text-xs uppercase tracking-wide text-amber-300">Problems</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {summary.problems.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ---- Skipped files, accounted for (Session BBV) ----

function SkippedAccounting({ skipped, onShowSkipped }: { skipped: KnowledgeDriveSkippedAccounting; onShowSkipped: () => void }) {
  const d = skipped.duplicates;
  return (
    <section aria-label="Skipped files accounted for" className="space-y-3 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading text-base text-ele-strong">Why {skipped.total} files were skipped</h3>
        <button type="button" onClick={onShowSkipped} className="text-xs text-ele-body underline hover:text-ele-strong">
          Show skipped files
        </button>
      </div>
      <ul className="grid gap-1 text-sm text-ele-body sm:grid-cols-2">
        {skipped.categories.map((c) => (
          <li key={c.key} className="flex justify-between gap-3">
            <span>{c.label}</span>
            <span className="font-menu text-ele-strong">{c.count}</span>
          </li>
        ))}
      </ul>
      {skipped.otherReasons.length > 0 && (
        <div>
          <p className="text-xs text-ele-body">The first reasons in Other:</p>
          <ul className="mt-1 space-y-1 text-xs text-ele-body">
            {skipped.otherReasons.map((r) => (
              <li key={r.reason}>
                {r.count} x {r.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      {d.total > 0 && (
        <div className="space-y-2 border-t border-ele-hairline pt-3">
          <p className="text-sm text-ele-body">
            {d.total} duplicates in {d.groups} groups: {d.originalSearchable} point at a searchable original, {d.originalNotSearchable} do not.
          </p>
          {d.fault && (
            <div role="alert" className="rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">
              <p>{d.message}</p>
              <ul className="mt-2 space-y-1 text-xs">
                {d.faultFiles.map((f) => (
                  <li key={f.id}>
                    {f.name}
                    {f.folder ? ` (${f.folder})` : ""}: {f.problemLabel}
                  </li>
                ))}
              </ul>
              {d.originalNotSearchable > d.faultFilesShown && <p className="mt-1 text-xs">Showing {d.faultFilesShown} of {d.originalNotSearchable}.</p>}
            </div>
          )}
          <p className="text-xs text-ele-body">{d.matchRule}</p>
        </div>
      )}
    </section>
  );
}

// ---- Counts by status (each sets the status filter) ----

function StatusCounts({
  status,
  selected,
  onSelect,
}: {
  status: KnowledgeDriveStatusResponse;
  selected: KnowledgeDriveStatus | "";
  onSelect: (s: KnowledgeDriveStatus | "") => void;
}) {
  const shown = KNOWLEDGE_DRIVE_STATUSES.filter((s) => (status.counts[s] ?? 0) > 0);
  const cls = (active: boolean) =>
    `max-sm:min-h-11 rounded-full border px-3 py-1 font-menu text-xs tracking-wide transition ${
      active ? "border-ele-purple-accent bg-ele-inset text-ele-green ring-1 ring-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
    }`;
  return (
    <div className="flex flex-wrap items-center gap-2" aria-label="Files by status">
      <button type="button" aria-pressed={selected === ""} onClick={() => onSelect("")} className={cls(selected === "")}>
        All {status.total}
      </button>
      {shown.map((s) => (
        <button key={s} type="button" aria-pressed={selected === s} onClick={() => onSelect(selected === s ? "" : s)} className={cls(selected === s)}>
          {KNOWLEDGE_DRIVE_STATUS_LABEL[s]} {status.counts[s]}
        </button>
      ))}
      {status.sourceMissing > 0 && (
        <span className="text-xs text-amber-300">{status.sourceMissing} no longer in Drive</span>
      )}
    </div>
  );
}

// ---- The file table ----

function SortHeader({
  label,
  column,
  sort,
  dir,
  onSort,
  className = "",
}: {
  label: string;
  column: KnowledgeDriveSort;
  sort: KnowledgeDriveSort;
  dir: "asc" | "desc";
  onSort: (c: KnowledgeDriveSort) => void;
  className?: string;
}) {
  const active = sort === column;
  return (
    <th className={`px-3 py-2 ${className}`} aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => onSort(column)} className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-ele-strong">
        {label}
        <span aria-hidden>{active ? (dir === "asc" ? "▲" : "▼") : ""}</span>
      </button>
    </th>
  );
}

export function FilesTable({
  rows,
  sort,
  dir,
  busy,
  onSort,
  onRetry,
  onReingest,
  onRemove,
  onForget,
}: {
  rows: KnowledgeDriveFileRow[];
  sort: KnowledgeDriveSort;
  dir: "asc" | "desc";
  busy: string | null;
  onSort: (c: KnowledgeDriveSort) => void;
  onRetry: (r: KnowledgeDriveFileRow) => void;
  onReingest: (r: KnowledgeDriveFileRow) => void;
  onRemove: (r: KnowledgeDriveFileRow) => void;
  onForget: (r: KnowledgeDriveFileRow) => void;
}) {
  return (
    <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
      <table className="ele-list-table w-full text-left text-sm">
        <colgroup>
          <col style={{ width: "24%" }} />
          <col className="max-md:hidden" style={{ width: "11%" }} />
          <col className="max-md:hidden" style={{ width: "9%" }} />
          <col className="max-md:hidden" style={{ width: "7%" }} />
          <col style={{ width: "17%" }} />
          <col className="max-md:hidden" style={{ width: "7%" }} />
          <col className="max-md:hidden" style={{ width: "6%" }} />
          <col className="max-md:hidden" style={{ width: "9%" }} />
          <col style={{ width: "10%" }} />
        </colgroup>
        <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
          <tr>
            <SortHeader label="Name" column="name" sort={sort} dir={dir} onSort={onSort} />
            <SortHeader label="Manufacturer" column="manufacturer" sort={sort} dir={dir} onSort={onSort} className="max-md:hidden" />
            <th className="max-md:hidden px-3 py-2">Type</th>
            <SortHeader label="Size" column="size" sort={sort} dir={dir} onSort={onSort} className="max-md:hidden" />
            <SortHeader label="Status" column="status" sort={sort} dir={dir} onSort={onSort} />
            <th className="max-md:hidden px-3 py-2">Pages</th>
            <th className="max-md:hidden px-3 py-2">Vision pages</th>
            <SortHeader label="Last synced" column="lastSyncedAt" sort={sort} dir={dir} onSort={onSort} className="max-md:hidden" />
            <th className="px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <FileRow
              key={row.id}
              row={row}
              busy={busy}
              onRetry={onRetry}
              onReingest={onReingest}
              onRemove={onRemove}
              onForget={onForget}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FileRow({
  row,
  busy,
  onRetry,
  onReingest,
  onRemove,
  onForget,
}: {
  row: KnowledgeDriveFileRow;
  busy: string | null;
  onRetry: (r: KnowledgeDriveFileRow) => void;
  onReingest: (r: KnowledgeDriveFileRow) => void;
  onRemove: (r: KnowledgeDriveFileRow) => void;
  onForget: (r: KnowledgeDriveFileRow) => void;
}) {
  const tone = knowledgeDriveStatusPill(row.status, row.sourceMissing);
  // The one visible action for the row: Retry when it can be retried, else the document it became.
  const primary: "retry" | "open" | null = row.canRetry ? "retry" : row.documentId ? "open" : null;
  const reason = row.statusReason ?? (row.status === "FAILED" ? row.lastError : null);
  return (
    <tr className="border-t border-ele-hairline align-top" data-testid={`drive-row-${row.id}`}>
      <td className="px-3 py-2">
        <span className="block truncate font-semibold text-ele-strong" title={row.name}>
          {row.name}
        </span>
        {row.memberPath && (
          <span className="block truncate text-xs text-ele-body opacity-80" title={row.memberPath}>
            Inside {memberArchiveName(row)}
          </span>
        )}
        {!row.memberPath && row.folderPath && (
          <span className="block truncate text-xs text-ele-body opacity-70" title={row.folderPath}>
            {row.folderPath}
          </span>
        )}
        {row.duplicateOfFileName && (
          <span className="block truncate text-xs text-ele-body opacity-80">Same as {row.duplicateOfFileName}</span>
        )}
      </td>
      <td className="max-md:hidden truncate px-3 py-2 text-ele-body">{row.manufacturer || "Not known"}</td>
      <td className="max-md:hidden truncate px-3 py-2 text-ele-body">{familyLabel(row.family)}</td>
      <td className="max-md:hidden px-3 py-2 text-ele-body">{formatFileSize(row.sizeBytes)}</td>
      <td className="px-3 py-2">
        <StatusPill word={row.statusLabel} tone={tone} />
        {row.sourceMissing && <span className="mt-1 block text-xs text-amber-300">No longer in Drive</span>}
        {reason && (
          <span className="mt-1 block truncate text-xs text-ele-body opacity-80" title={reason}>
            {reason}
          </span>
        )}
      </td>
      <td className="max-md:hidden px-3 py-2 text-ele-body">{pagesText(row)}</td>
      <td className="max-md:hidden px-3 py-2 text-ele-body">{row.visionPagesUsed}</td>
      <td className="max-md:hidden px-3 py-2 text-ele-body">{knowledgeShortDate(row.lastSyncedAt)}</td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          {primary === "retry" && (
            <Button size="sm" variant="secondary" onClick={() => onRetry(row)} loading={busy === `retry:${row.id}`} disabled={busy !== null}>
              Retry
            </Button>
          )}
          {primary === "open" && row.documentId && (
            <Link to={`/office/knowledge/${row.documentId}`} className="font-menu text-xs tracking-wide text-ele-blue underline hover:text-ele-strong">
              Open document
            </Link>
          )}
          <OverflowMenu label={`More actions for ${row.name}`}>
            <a href={row.openOriginalUrl} target="_blank" rel="noopener noreferrer" className={overflowItemCls}>
              Open original
            </a>
            {row.documentId && primary !== "open" && (
              <Link to={`/office/knowledge/${row.documentId}`} className={overflowItemCls}>
                Open document
              </Link>
            )}
            {row.canReingest && (
              <ConfirmButton
                onConfirm={() => onReingest(row)}
                label="Re-ingest"
                confirmLabel="Press again to re-ingest"
                busy={busy === `reingest:${row.id}`}
                busyLabel="Queueing..."
                disabled={busy !== null}
                className={overflowItemCls}
              />
            )}
            {row.canRemove && (
              <button type="button" onClick={() => onRemove(row)} className={overflowDangerItemCls}>
                Remove from knowledge base
              </button>
            )}
            {row.canForget && (
              <button type="button" onClick={() => onForget(row)} className={overflowDangerItemCls}>
                Forget
              </button>
            )}
          </OverflowMenu>
        </div>
      </td>
    </tr>
  );
}
